import { Capacitor, registerPlugin } from "@capacitor/core";
import { GC_ACHIEVEMENTS, GC_LEADERBOARDS } from "../game/gameCenterIds";
import { useGame } from "../game/store";
import { STARTER_PACK_VEHICLE } from "../game/vehicles";
import type { DifficultyId } from "../game/difficulty";

type GcResult = { ok: boolean; reason?: string; alias?: string };

type NativeGameCenterPlugin = {
  authenticate(): Promise<GcResult & { playerId?: string }>;
  submitScore(options: { leaderboardId: string; score: number }): Promise<GcResult>;
  reportAchievement(options: {
    achievementId: string;
    percent?: number;
  }): Promise<GcResult>;
  showDashboard(options?: { state?: "leaderboards" | "achievements" }): Promise<GcResult>;
};

const NativeGameCenter = registerPlugin<NativeGameCenterPlugin>("NativeGameCenter");

let authOnce: Promise<boolean> | null = null;

function native(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
}

export async function authenticateGameCenter(): Promise<boolean> {
  if (!native()) return false;
  if (!authOnce) {
    authOnce = NativeGameCenter.authenticate()
      .then((res) => {
        const ok = Boolean(res.ok);
        if (!ok) authOnce = null;
        return ok;
      })
      .catch(() => {
        authOnce = null;
        return false;
      });
  }
  return authOnce;
}

export async function showGameCenter(
  state: "leaderboards" | "achievements" = "leaderboards",
): Promise<GcResult> {
  if (!native()) return { ok: false, reason: "web-noop" };
  const authed = await authenticateGameCenter();
  if (!authed) return { ok: false, reason: "not_authenticated" };
  try {
    return await NativeGameCenter.showDashboard({ state });
  } catch (e) {
    return { ok: false, reason: e instanceof Error ? e.message : "error" };
  }
}

/** Push current run + lifetime unlocks. Safe to call often; GameKit is idempotent at 100%. */
export async function syncGameCenter(opts?: {
  runScore?: number;
  difficulty?: DifficultyId;
}): Promise<void> {
  if (!native()) return;
  const authed = await authenticateGameCenter();
  if (!authed) return;

  const s = useGame.getState();
  const difficulty = opts?.difficulty ?? s.difficulty;
  const runScore = opts?.runScore ?? Math.floor(s.distance);
  const board = GC_LEADERBOARDS[difficulty];
  if (board && runScore > 0) {
    void NativeGameCenter.submitScore({
      leaderboardId: board,
      score: runScore,
    }).catch(() => undefined);
  }

  const best = Math.max(
    s.bestByDifficulty.easy,
    s.bestByDifficulty.medium,
    s.bestByDifficulty.hard,
  );
  const earned: string[] = [];
  if (s.gamesPlayed >= 1) earned.push(GC_ACHIEVEMENTS.firstDash);
  if (s.gamesPlayed >= 3) earned.push(GC_ACHIEVEMENTS.medium);
  if (s.gamesPlayed >= 12) earned.push(GC_ACHIEVEMENTS.hard);
  if (best >= 400 || runScore >= 400) earned.push(GC_ACHIEVEMENTS.m400);
  if (best >= 1000 || runScore >= 1000) earned.push(GC_ACHIEVEMENTS.m1000);
  if (s.vehiclesOwned.length >= 2) earned.push(GC_ACHIEVEMENTS.fleet);
  if (s.starterOwned || s.vehiclesOwned.includes(STARTER_PACK_VEHICLE)) {
    earned.push(GC_ACHIEVEMENTS.phantom);
  }
  if (s.streakCount >= 7) earned.push(GC_ACHIEVEMENTS.streak7);

  for (const achievementId of earned) {
    void NativeGameCenter.reportAchievement({
      achievementId,
      percent: 100,
    }).catch(() => undefined);
  }
}
